// pluginView.js
// Plugin view following ProseMirror best practices

import {initializeStoreSubscription, subscribeToCitationData} from './storeAdapter';
import {referenceCitationPluginKey} from './index';

/**
 * Create plugin view for managing side effects
 * @param {EditorView} editorView - ProseMirror editor view
 * @param {string} reportId - Report ID
 * @param {import('yjs').Map} yReferenceMarks - Y.Map("referenceMarks") for citation numbering
 * @returns {Object} Plugin view object
 */
export function createPluginView(editorView, reportId, yReferenceMarks) {
    // Initialize Y.Map subscription for citation numbering
    initializeStoreSubscription(yReferenceMarks);

    let unsubscribe = null;
    let destroyed = false;

    const DEBUG_REFERENCE_CITATION =
        typeof window !== 'undefined' &&
        (window.__DEBUG_REFERENCE_CITATION__ || localStorage.getItem('DEBUG_REFERENCE_CITATION') === '1');

    const dispatchInvalidate = () => {
        if (destroyed) return;
        if (DEBUG_REFERENCE_CITATION) {
            console.log('[referenceCitation] dispatchInvalidate');
        }
        const tr = editorView.state.tr;
        tr.setMeta(referenceCitationPluginKey, {invalidate: true});
        tr.setMeta('addToHistory', false);
        editorView.dispatch(tr);
    };

    // Always subscribe. reportId is unused (flat store) and can be missing when
    // the editor mounts before snippetDrop.reportId is available — gating on it
    // left decorations stale until the next docChanged (MINSKY-16782).
    if (DEBUG_REFERENCE_CITATION) {
        console.log('[referenceCitation] subscribeToCitationData', {reportId});
    }
    unsubscribe = subscribeToCitationData(reportId, () => {
        if (DEBUG_REFERENCE_CITATION) {
            console.log('[referenceCitation] storeChanged');
        }
        // Sync first so the `[n]` widget appears in the same tick as the Y-map
        // write (classification-level regression). Retry on a microtask in case
        // this callback ran inside another plugin's view.update, where
        // EditorView.dispatch is dropped or overwritten.
        try {
            dispatchInvalidate();
        } catch (_err) {
            // Nested dispatch during an in-progress view update.
        }
        queueMicrotask(() => {
            if (!destroyed) dispatchInvalidate();
        });
    });

    // Defer the mount-time call: dispatching from inside a plugin's own view()
    // constructor (called synchronously during `new EditorView(...)`) is unsafe since
    // the view isn't fully built yet. A microtask is enough — this only needs to catch
    // store data that was already pending/confirmed before the view existed (common
    // right after a drop before Firestore confirms).
    queueMicrotask(() => {
        if (!destroyed) dispatchInvalidate();
    });

    return {
        destroy() {
            destroyed = true;
            if (unsubscribe) {
                unsubscribe();
            }
        },
    };
}
